import { authFetch, clearToken } from "../utils/auth";
import { Component } from "react";

const API = `${window.location.origin}/api`;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error) {
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error, errorInfo) {
    try {
      fetch(`${API}/client-errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error?.message || String(error),
          stack: error?.stack || "",
          component_stack: errorInfo?.componentStack || "",
          route: window.location.pathname,
          build_version: window.__GFP_VERSION || "unknown",
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          type: "react_error_boundary"
        })
      }).catch(() => {});
    } catch (e) {}
  }

  handleClearCacheReload = () => {
    if ("caches" in window)
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
    if ("serviceWorker" in navigator)
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => regs.forEach((r) => r.unregister()));
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen bg-gray-50 flex items-center justify-center p-6"
          data-testid="error-boundary-screen"
        >
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2
              className="text-[#111] font-bold text-lg mb-2"
              data-testid="error-boundary-title"
            >
              Something went wrong
            </h2>
            <p className="text-[#555] text-sm mb-2">
              The app encountered an unexpected error.
            </p>
            <p
              className="text-gray-600 text-xs mb-6 font-mono"
              data-testid="error-boundary-meta"
            >
              Error ID: {this.state.errorId} | Build:{" "}
              {window.__GFP_VERSION || "unknown"}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-colors"
                data-testid="error-boundary-reload-btn"
              >
                Reload App
              </button>
              <button
                onClick={this.handleClearCacheReload}
                className="w-full py-3 bg-gray-200 hover:bg-white/15 text-[#111] rounded-xl text-sm transition-colors"
                data-testid="error-boundary-clear-cache-btn"
              >
                Clear Cache & Reload
              </button>
              <button
                onClick={() => { window.location.href = "/"; }}
                className="w-full py-3 bg-gray-200 hover:bg-white/15 text-[#111] rounded-xl text-sm transition-colors"
                data-testid="error-boundary-login-btn"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
